import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MojeRaportyGodzinoweFiltryModel } from 'src/app/modele/administracja/mojeRaportyGodzinoweFiltry';
import { RaportyGodzinoweDaneResponse } from 'src/app/modele/administracja/raportyGodzinoweDane';
import { RejestrFiltrowanyRequest } from 'src/app/modele/wspolne/rejestrFiltrowanyRequest';
import { Routing } from 'src/app/modele/wspolne/routing';
import { Stronicowanie } from 'src/app/modele/wspolne/stronicowanie';
import { Uprawnienie } from 'src/app/modele/wspolne/uprawnienie';
import { RaportyService } from 'src/app/serwisy/administracja/raporty.service';
import { AuthService } from 'src/app/serwisy/ogolne/auth.service';
import { LoaderService } from 'src/app/serwisy/ogolne/loader.service';
import { BazowyComponent } from '../bazowy.component';
import { ZakresDatWynik } from '../wspolne/zakres-dat/zakres-dat.component';
import {
  KierunekSortowania,
  Sortowanie,
} from 'src/app/modele/wspolne/sortowanie';

export interface GodzinyDnia {
  data: string;
  dataFormatowana: string;
  godziny: number | null;
}

export interface RaportGodzinowy {
  id: string;
  pracownikId: string;
  imieNazwisko: string;
  projektId: string;
  projektNazwa: string;
  domyslneGodziny: number;
  godzinyDni: GodzinyDnia[];
}

@Component({
  selector: 'app-moje-raporty-godzinowe',
  templateUrl: './moje-raporty-godzinowe.component.html',
  styleUrls: ['./moje-raporty-godzinowe.component.css'],
})
export class MojeRaportyGodzinoweComponent
  extends BazowyComponent
  implements OnInit
{
  czyZaladowanoDane: boolean = false;

  // Sortowanie
  kierunekSortowania: { suma: string; projekt: string } = {
    suma: '',
    projekt: '',
  };
  raportyWyswietlane: RaportGodzinowy[] = [];

  // Paginacja
  rozmiarTabeli: number = 25;
  strona: number = 1;
  calkowitaLiczbaElementow: number = 0;
  czyJestWiecejRekordow: boolean = false;

  // Filtry
  dataOd: string = '';
  dataDo: string = '';

  // Projekty do filtrowania
  projekty: { id: string; nazwa: string }[] = [];
  wybraneProjektyId: string[] = [];

  // Lista dat (kolumny)
  daty: { data: string; dataFormatowana: string }[] = [];

  // Lista raportow
  raporty: RaportGodzinowy[] = [];

  constructor(
    public router: Router,
    modalService: NgbModal,
    loaderSerwis: LoaderService,
    komunikatSerwis: ToastrService,
    public authService: AuthService,
    public translateSerwis: TranslateService,
    private raportySerwis: RaportyService,
    private activatedRoute: ActivatedRoute
  ) {
    super(
      authService,
      modalService,
      loaderSerwis,
      komunikatSerwis,
      router,
      translateSerwis
    );
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (!this.czyMaDostepDoMoichRaportow()) {
        this.router.navigate([Routing.StronaGlowna]);
        return;
      }
      this.pobierzFiltryZUrl();
      this.zaladujDaneStartowe();
    });
  }

  private zaladujDaneStartowe(): void {
    this.pobierzDaneDoEkranu(true);
  }

  czyMaDostepDoMoichRaportow(): boolean {
    const daneUzytkownika = this.authService.pobierzDaneZalogowanegoUzytkownika();
    return (
      daneUzytkownika.uprawnienie === Uprawnienie.wykonawca ||
      daneUzytkownika.uprawnienie === Uprawnienie.wykonawcaPlus ||
      daneUzytkownika.uprawnienie === Uprawnienie.administrator
    );
  }

  pobierzDaneDoEkranu(aktualizujProjekty: boolean = false) {
    const model = new RejestrFiltrowanyRequest<MojeRaportyGodzinoweFiltryModel>();
    model.filtry = {
      dataOd: this.dataOd,
      dataDo: this.dataDo,
      projektyIds:
        this.wybraneProjektyId.length > 0 ? this.wybraneProjektyId : null,
    };
    model.stronicowanie = new Stronicowanie(false);
    model.stronicowanie.iloscRekordow = this.rozmiarTabeli;
    model.stronicowanie.strona = this.strona;
    model.sortowanie = this.budujSortowanie();

    this.raportySerwis
      .pobierzMojeRaportyGodzinowe(model)
      .subscribe((rezultat) => {
        if (this.czyRezultatOk(rezultat)) {
          this.przetworzOdpowiedzApi(rezultat.element, aktualizujProjekty);
          this.czyZaladowanoDane = true;
        }
      });
  }

  private budujSortowanie(): Sortowanie {
    if (this.kierunekSortowania.projekt === 'up') {
      return new Sortowanie('projektNazwa', KierunekSortowania.asc);
    } else if (this.kierunekSortowania.projekt === 'down') {
      return new Sortowanie('projektNazwa', KierunekSortowania.desc);
    } else if (this.kierunekSortowania.suma === 'up') {
      return new Sortowanie('sumaWiersza', KierunekSortowania.asc);
    } else if (this.kierunekSortowania.suma === 'down') {
      return new Sortowanie('sumaWiersza', KierunekSortowania.desc);
    }
    return new Sortowanie('projektNazwa', KierunekSortowania.asc);
  }

  private przetworzOdpowiedzApi(dane: RaportyGodzinoweDaneResponse, aktualizujProjekty: boolean = false): void {
    if (aktualizujProjekty && dane.projekty) {
      this.projekty = dane.projekty
        .map((p) => ({
          id: p.id,
          nazwa: `${p.nazwaProjektu} (${this.formatujDateMiesiac(p.dataUtworzenia)})`,
        }))
        .sort((a, b) => a.nazwa.localeCompare(b.nazwa));
    }

    this.daty = dane.daty.map((data) => {
      const dataObj = new Date(data);
      const dzien = dataObj.getDate().toString().padStart(2, '0');
      const miesiac = (dataObj.getMonth() + 1).toString().padStart(2, '0');
      return {
        data: data,
        dataFormatowana: `${dzien}.${miesiac}`,
      };
    });

    this.raporty = dane.wiersze.map((wiersz) => ({
      id: wiersz.id,
      pracownikId: wiersz.pracownikId,
      imieNazwisko: wiersz.imieNazwisko,
      projektId: wiersz.projektId || '',
      projektNazwa: wiersz.projektNazwa || '',
      domyslneGodziny: wiersz.domyslneGodziny,
      godzinyDni: wiersz.godzinyDni.map((godzinyDnia) => {
        const dataObj = new Date(godzinyDnia.data);
        const dzien = dataObj.getDate().toString().padStart(2, '0');
        const miesiac = (dataObj.getMonth() + 1).toString().padStart(2, '0');
        return {
          data: godzinyDnia.data,
          dataFormatowana: `${dzien}.${miesiac}`,
          godziny: godzinyDnia.godziny,
        };
      }),
    }));

    this.calkowitaLiczbaElementow = dane.liczbaWszystkich;
    this.strona = dane.strona;
    this.czyJestWiecejRekordow =
      dane.strona * dane.rozmiarStrony < dane.liczbaWszystkich;

    this.raportyWyswietlane = this.raporty;
  }

  obliczSumePracownika(raport: RaportGodzinowy): number {
    return raport.godzinyDni.reduce(
      (suma, dzien) => suma + (dzien.godziny || 0),
      0
    );
  }

  obliczSumeDnia(data: string): number {
    return this.raporty.reduce((suma, raport) => {
      const dzien = raport.godzinyDni.find((d) => d.data === data);
      return suma + (dzien?.godziny || 0);
    }, 0);
  }

  obliczSumeCalkowita(): number {
    return this.raporty.reduce(
      (suma, raport) => suma + this.obliczSumePracownika(raport),
      0
    );
  }

  pobierzGodzinyDnia(raport: RaportGodzinowy, data: string): number | null {
    const dzien = raport.godzinyDni.find((d) => d.data === data);
    return dzien ? dzien.godziny : null;
  }

  zmienSortowanie(kolumna: 'suma' | 'projekt') {
    const kolumny: ('suma' | 'projekt')[] = ['suma', 'projekt'];
    kolumny
      .filter((k) => k !== kolumna)
      .forEach((k) => (this.kierunekSortowania[k] = ''));

    const obecny = this.kierunekSortowania[kolumna];
    if (obecny === '') this.kierunekSortowania[kolumna] = 'up';
    else if (obecny === 'up') this.kierunekSortowania[kolumna] = 'down';
    else this.kierunekSortowania[kolumna] = '';

    this.strona = 1;
    this.pobierzDaneDoEkranu();
  }

  zmienRozmiarTabeli(): void {
    this.strona = 1;
    this.pobierzDaneDoEkranu();
  }

  zmienStrone(oIle: number): void {
    this.strona += oIle;
    this.pobierzDaneDoEkranu();
  }

  pobierzNumerRekorduPoczatek(): number {
    return (this.strona - 1) * this.rozmiarTabeli + 1;
  }

  pobierzNumerRekorduKoniec(): number {
    const koniec = this.strona * this.rozmiarTabeli;
    return Math.min(koniec, this.calkowitaLiczbaElementow);
  }

  pobierzFiltryZUrl(): void {
    const params = this.activatedRoute.snapshot.queryParamMap;

    const domyslnyZakres = this.obliczAktualnyMiesiac();
    this.dataOd = params.get('dataOd') || domyslnyZakres.od;
    this.dataDo = params.get('dataDo') || domyslnyZakres.do;

    const projektyParam = params.get('projekty');
    this.wybraneProjektyId = projektyParam ? projektyParam.split(',') : [];
  }

  zapiszFiltryWUrl(): void {
    this.router.navigate([], {
      queryParams: this.budujQueryParams(),
      queryParamsHandling: '',
      replaceUrl: true,
      relativeTo: this.activatedRoute,
    });
  }

  budujQueryParams(): object {
    return {
      dataOd: this.dataOd,
      dataDo: this.dataDo,
      projekty:
        this.wybraneProjektyId.length > 0
          ? this.wybraneProjektyId.join(',')
          : null,
    };
  }

  obliczAktualnyMiesiac(): { od: string; do: string } {
    const dzis = new Date();
    const pierwszyDzien = new Date(dzis.getFullYear(), dzis.getMonth(), 1);
    const ostatniDzien = new Date(dzis.getFullYear(), dzis.getMonth() + 1, 0);

    return {
      od: this.formatujDate(pierwszyDzien),
      do: this.formatujDate(ostatniDzien),
    };
  }

  private formatujDate(data: Date): string {
    const rok = data.getFullYear();
    const miesiac = (data.getMonth() + 1).toString().padStart(2, '0');
    const dzien = data.getDate().toString().padStart(2, '0');
    return `${rok}-${miesiac}-${dzien}`;
  }

  private formatujDateMiesiac(data: string | Date): string {
    const d = new Date(data);
    const rok = d.getFullYear();
    const miesiac = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${rok}-${miesiac}`;
  }

  zastosujFiltry(): void {
    this.zapiszFiltryWUrl();
    this.strona = 1;
    this.pobierzDaneDoEkranu();
  }

  naZakresZmieniony(zakres: ZakresDatWynik): void {
    this.dataOd = zakres.dataOd;
    this.dataDo = zakres.dataDo;
    this.zastosujFiltry();
  }

  zaznaczWszystkieProjekty(): void {
    this.wybraneProjektyId = this.projekty.map((p) => p.id);
    this.zastosujFiltry();
  }

  odznaczWszystkieProjekty(): void {
    this.wybraneProjektyId = [];
    this.zastosujFiltry();
  }

  eksportujDane() {
    const filtry: MojeRaportyGodzinoweFiltryModel = {
      dataOd: this.dataOd,
      dataDo: this.dataDo,
      projektyIds:
        this.wybraneProjektyId.length > 0 ? this.wybraneProjektyId : null,
    };

    this.raportySerwis.eksportujMojeRaportyGodzinowe(filtry).subscribe((rezultat) => {
      if (this.czyRezultatOk(rezultat)) {
        var a = document.createElement('a');
        a.target = '_blank';
        a.href =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          rezultat.element.plik;
        a.download = rezultat.element.nazwaPliku;
        a.click();
        return;
      }

      this.wyswietlKomunikat(rezultat.wiadomosc, true);
    });
  }
}
